import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { getToken } from '../../utils/tokenUtils';
import Drawer from "../../components/UI/Drawer";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import DeleteButton from "../../components/UI/AlertDialog";

const CheckOutApprovalPage = () => {
    const [checkOutData, setCheckOutData] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = getToken();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [notification, setNotification] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null); // "approved" or "rejected"
    const [approvalComment, setApprovalComment] = useState("");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [filter, setFilter] = useState("All");
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 15,
        });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [isFiltering, setIsFiltering] = useState(false);
    const [editAttendanceData, setEditAttendanceData] = useState({
        check_out_time: "",
    });
    // filter
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const [filteredData, setFilteredData] = useState(checkOutData);
    
    const [tempFilters, setTempFilters] = useState({});
    
    const toggleFilterMenu = () => {
        setShowFilterMenu(!showFilterMenu);
    };

    const handleFilterChange = (field, value) => {
        setActiveFilters(prev => ({
        ...prev,
        [field]: value
        }));
    };

    const clearFilters = () => {
        setActiveFilters({});
        setSearchQuery("");
    };

    const clearFilterByKey = (key) => {
        const updatedFilters = { ...activeFilters };
        delete updatedFilters[key];
        setActiveFilters(updatedFilters);
    };

    const filterableColumns = [
        { field: "employee_id", label: "Employee ID" },
        { field: "approval_status", label: "Status" },
    ];

    useEffect(() => {
        // Apply both search query and filters
        let result = checkOutData;

        // Apply search query filtering
        if (searchQuery) {
        const lowercasedQuery = searchQuery.toLowerCase();
        result = result.filter((item) => {
            // console.log(item)
            // Search across all relevant fields
            return (
            (item.attendance.employee_id?.toLowerCase().includes(lowercasedQuery)) ||
            (item.approval_status?.toLowerCase().includes(lowercasedQuery))
            );
        });
        }

        // Apply column-specific filters
        Object.entries(activeFilters).forEach(([field, filterValue]) => {
        if (filterValue) {
            result = result.filter(item => {
            const value = item[field];
            if (field === 'created_at' && value) {
                // Date filtering
                const itemDate = new Date(value);
                const day = String(itemDate.getDate()).padStart(2, '0');
                const month = String(itemDate.getMonth() + 1).padStart(2, '0');
                const year = itemDate.getFullYear();
                const formattedDate = `${day}-${month}-${year}`;
                
                return formattedDate.includes(filterValue);
            } else {
                // Standard string filtering
                return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
            }
            });
        }
        });

        const filteringNow = searchQuery.length > 0 || Object.values(activeFilters).some(v => !!v);
        setIsFiltering(filteringNow);
        setFilteredData(result);
    }, [searchQuery, activeFilters, checkOutData]);
        
    useEffect(() => {
    if (editModalOpen && selectedRequest) {
        setEditAttendanceData({
            check_out_time: selectedRequest.attendance.check_out_time?.slice(0, 16) || "",
        });
    }
    }, [editModalOpen, selectedRequest]);
    

    // console.log("CheckOut Data", selectedRequest);

    const handleEditClick = (record) => {
        setSelectedRequest(record);
        setEditModalOpen(true);
    };
    
    const openDrawerForDetails = (employee) => {
        setSelectedRequest(employee);
        setIsDrawerOpen(true);
    };
    
    const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedRequest(null);
    };
    
    // Fetch CheckOuts
    const fetchCheckOuts = async (page = 1) => {
        try {
        const res = await axios.get(`https://hrmsnew.prepstripe.com/api/pending-checkout?page=${page}`, {
            headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            },
        });
        setCheckOutData(res.data.data.data);
        setPagination({
            currentPage: res.data?.data.current_page || 1,
            totalPages: res.data?.data.last_page || 1,
            totalItems: res.data?.data.total || 0,
            perPage: res.data?.data.per_page || 15,
        });
        } catch (error) {
        console.error("Error fetching check-in data:", error);
        } finally {
        setLoading(false);
        }
    };

    useEffect(() => {
        fetchCheckOuts();
    }, [token]);

    // Handle Approve/Reject
    const handleApproval = async () => {
        if (!selectedRequest || !selectedStatus) return;

        setIsSubmitting(true);

        try {
            await axios.post(
            `https://hrmsnew.prepstripe.com/api/attendance/${selectedRequest.attendance_id}/review-checkout`,
            {
                approval_status: selectedStatus,
                manager_remark: approvalComment,
            },
            {
                headers: {
                Authorization: `Bearer ${token}`,
                },
            }
            );

            fetchCheckOuts(); // Refresh list
            closeApprovalModal();
            setIsDrawerOpen(false);
            setNotification("Request status updated successfully");
        } catch (err) {
            console.error("Error submitting approval:", err);
            const errorMsg = err.response?.data?.error || "Something went wrong";
            setNotification(errorMsg);
        } finally {
            setIsSubmitting(false);
            // setIsDrawerOpen(false);
        }
    };

    // update particular attendance
    const handleUpdateAttendance = async (event) => {
        event.preventDefault();

        try {
            const response = await axios.put(
            `https://hrmsnew.prepstripe.com/api/attendance/${selectedRequest.id}`,
            editAttendanceData,
            {
                headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                },
            }
            );

            setNotification(response?.message || "Attendance updated successfully.");
            setEditModalOpen(false);
            fetchCheckOuts();
        } catch (error) {
            console.error(error);
            if (error.response) {
            setNotification(error.response.data.message || "Update failed");
            } else {
            setNotification("Something went wrong!");
            }
        }
    };

    // delete particular attendance
    const deleteAttendance = async (attendanceId) => {
        if (!attendanceId) return;

        try {
            const response = await fetch(`https://hrmsnew.prepstripe.com/api/attendance/${attendanceId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    // Include authentication headers if needed
                },
                // credentials: 'include', // if using cookies for auth
            });

            const result = await response.json();

            if (response.ok) {
                setNotification(result.message); // or use toast
                fetchCheckOuts();
            } else {
                setNotification(`Error: ${result.error || 'Failed to delete attendance'}`);
            }
        } catch (error) {
            console.error('Delete failed:', error);
            setNotification('An error occurred while deleting the attendance record.');
        }
    };

    // console.log("Total Pages", pagination?.totalPages)

    const openApprovalModal = (request, status) => {
        setSelectedRequest(request);
        setSelectedStatus(status);
        setApprovalComment(""); // Clear previous comment
        setIsModalOpen(true);
      };
      
    const closeApprovalModal = () => {
        setIsModalOpen(false);
    };

    // Pagination
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
        fetchCheckOuts(newPage);
        }
    };

    // Render Page Numbers
    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(
            <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-4 py-2 rounded-md mx-1 ${
                currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            >
            {i}
            </button>
        );
        }
        return pages;
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        const filtering = value !== '';
        setIsFiltering(filtering);
      
        if (!filtering) {
          // Search cleared, restore current page's data
          handlePageChange(currentPage); // fetchResponses will be called inside
        }
    };

    const filteredCheckOuts = checkOutData
    .filter((checkout) => {
      // Status filter
      if (filter === "All") return true;
      return checkout.approval_status === filter;
    })
    .filter((checkout) => {
      // Search filter
      const empId = checkout.attendance?.employee_id?.toString() || "";
      const status = checkout.attendance?.approval_status?.toString() || "";
      const search = searchTerm.toLowerCase();
      return empId.includes(search) || status.includes(search);
    });

    const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

    return (
        <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
            {/* notification compo */}
            {notification && (
                <Notification message={notification} onClose={() => setNotification(null)} />
            )}

            {/* Breadcrumb */}
            <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
                    <li>
                        <div className="flex items-center">
                            <Link
                            to="/admin/attendance_management"
                            className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
                            >
                            Attendance Management
                            </Link>
                        </div>
                    </li>
                    <li aria-current="page">
                        <div className="flex items-center">
                            <svg className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                            </svg>
                            <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Pending CheckOuts</span>
                        </div>
                    </li>
                </ol>
            </nav>

            <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">Pending Check-Out Approvals</h2>
                
            {/* Search and filter controls */}
            <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">

                {/* search field */}
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full dark:bg-black dark:text-white dark:border-gray-700 sm:w-64 placeholder:text-xs md:placeholder:text-sm p-2 border border-gray-300 rounded-md"
                />

                {/* filter button */}
                <div className="flex justify-between gap-2 w-full sm:w-auto">
                    <div className="relative">
                        <button 
                            onClick={toggleFilterMenu}
                            className="p-2 dark:bg-gray-800 dark:text-white dark:border-gray-700 border border-[#F4F7FF] rounded-md flex gap-1 items-center bg-[#F4F7FF] hover:bg-gray-200"
                            >
                            <svg  className="dark:text-white" width="18" height="17" viewBox="0 0 13 14" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.98734 13.5608C7.85034 13.5608 7.71598 13.5252 7.59884 13.4581L5.09834 12.0415C4.817 11.8812 4.58412 11.6552 4.42215 11.3852C4.26017 11.1153 4.17457 10.8104 4.17359 10.5001V7.29493C4.17408 7.06871 4.0996 6.84803 3.96059 6.6638L0.469345 2.02988C0.387822 1.92291 0.339174 1.79674 0.328769 1.66531C0.318364 1.53387 0.346605 1.40228 0.410378 1.28504C0.474151 1.16781 0.570975 1.06949 0.690169 1.00093C0.809362 0.932381 0.94629 0.896259 1.08584 0.896552H11.8333C11.973 0.896002 12.1101 0.931967 12.2295 1.00047C12.3488 1.06896 12.4458 1.16732 12.5096 1.28465C12.5734 1.40198 12.6016 1.5337 12.5911 1.66523C12.5805 1.79677 12.5317 1.92297 12.4498 2.02988L8.95859 6.6638C8.81933 6.84794 8.74458 7.06863 8.74484 7.29493V12.844C8.74465 13.0338 8.66482 13.2158 8.52285 13.3502C8.38088 13.4845 8.18832 13.5603 7.98734 13.5608ZM1.08584 1.60843L4.57109 6.25297C4.80208 6.55645 4.92547 6.92118 4.92359 7.29493V10.4994C4.92387 10.6874 4.9756 10.8722 5.07376 11.0358C5.17192 11.1994 5.31319 11.3362 5.48384 11.433L7.98434 12.8497L7.99484 7.29422C7.99319 6.92039 8.11685 6.55566 8.34809 6.25226L11.8386 1.61834L1.08584 1.60843Z" fill="currentColor"/>
                            </svg>
                            <span className="ml-1">Filter</span>
                        </button>
                        
                        {showFilterMenu && (
                        <div className="absolute dark:bg-gray-900 dark:border-gray-700 z-10 mt-2 md:right-0 right-auto left-0 md:left-auto bg-white border border-gray-200 rounded-md shadow-lg p-4 w-64">
                            <div className="flex justify-between items-center mb-3">
                            <h3 className="font-medium dark:text-white">Filters</h3>
    
                            {hasTempFilters && (
                                <button
                                    onClick={() => {
                                    setActiveFilters(tempFilters);
                                    setShowFilterMenu(false);
                                    }}
                                    className=" bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 text-sm"
                                >
                                    Apply Filters
                                </button>
                            )}
                            </div>
                            
                            <div className="space-y-3">
                            {filterableColumns.map((column) => (
                                <div key={column.field} className="space-y-1">
                                <label className="block text-sm dark:text-gray-400 font-medium text-gray-700 font-medium text-gray-700">
                                    {column.label}
                                </label>
                                {column.field === "approval_status" ? (
                                    // Dropdown for status filter
                                    <select
                                    value={tempFilters[column.field] || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setTempFilters(prev => ({ ...prev, [column.field]: value }));
                                    }}
                                    // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                                    className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2 text-sm border border-gray-300 rounded-md"
                                    >
                                    <option value="">All</option>
                                    <option value="approved">Approved</option>
                                    <option value="pending">Pending</option>
                                    </select>
                                ) : (
                                    // Text input for other filters
                                    <input
                                    type="text"
                                    value={tempFilters[column.field] || ""}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setTempFilters(prev => ({ ...prev, [column.field]: value }));
                                    }}
                                    // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                                    placeholder={`Filter by ${column.label.toLowerCase()}`}
                                    className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2 text-sm border border-gray-300 rounded-md"
                                    />
                                )}
                                </div>
                            ))}
                            </div>
                            
                            {isFiltering && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-sm text-gray-600">
                                Showing {filteredData.length} of {checkOutData.length} results
                                </p>
                            </div>
                            )}
                        </div>
                        )}
                    </div>

                    {isFiltering && (
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(activeFilters).map(([key, value]) => (
                        value && (
                            <div
                            key={key}
                            className="flex items-center gap-2 px-3 py-1.5 border border-blue-500 bg-blue-50 dark:bg-gray-900 text-blue-600 rounded-full text-sm shadow-sm"
                            >
                            <span className="capitalize dark:text-white">{key.replace('_', ' ')}: {value}</span>
                            <button
                                onClick={() => clearFilterByKey(key)}
                                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-blue-100 focus:outline-none"
                                aria-label={`Clear ${key}`}
                            >
                                <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                                >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            </div>
                        )
                        ))}
                        <button
                        onClick={clearFilters}
                        className="ml-2 text-sm text-red-500 hover:underline"
                        >
                        Clear All
                        </button>
                    </div>
                    )}
                </div>

            </div>

            {/* Requests Table */}
            <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
                <table className="min-w-full table-auto md:table-fixed text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
                        <tr className="text-sm sm:text-base">
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Employee ID</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Check-Out</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Remark</th>
                            <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">Status</th>
                            <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                            <td colSpan="6" className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">
                                <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                            </td>
                        </tr>
                    ) : 
                    filteredCheckOuts.length === 0 ? (
                        <tr>
                            <td
                            colSpan="6"
                            className="text-center dark:text-white text-gray-500 p-2"
                            >
                            No Data Found
                            </td>
                        </tr>
                    ) : (
                        filteredCheckOuts.map((entry) => (
                        <tr key={entry.id} className="border-b dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white  hover:bg-gray-100">
                            <td className="border-b dark:text-white  p-2 md:text-md text-sm text-center whitespace-nowrap">{entry.attendance.employee_id}</td>
                            <td className="border-b dark:text-white  p-2 md:text-md text-sm text-center whitespace-nowrap">
                            {entry.attendance.check_out_time
                                ? (() => {
                                    // Parse time string (assumes format "HH:mm:ss" or "HH:mm")
                                    const [hourStr, minuteStr] = entry.attendance.check_out_time.split(':');
                                    let hour = parseInt(hourStr, 10);
                                    const minute = minuteStr || '00';
                                    const ampm = hour >= 12 ? 'PM' : 'AM';
                                    hour = hour % 12 || 12; // convert 0 -> 12 for 12 AM
                                    return `${hour}:${minute} ${ampm}`;
                                })()
                                : '-'}
                            </td>
                            <td className="border-b px-4 md:text-md text-sm whitespace-nowrap py-2 border text-center capitalize">{entry.manager_remark || '-'}</td>
                            <td className={`border-b px-4 py-2 md:text-md text-sm whitespace-nowrap border text-center capitalize ${entry.approval_status === "pending" ? "text-yellow-500": "text-green-500"}`}>{entry.approval_status}</td>
                            <td className="border-b p-2 flex justify-center items-center gap-2">
                                <button onClick={() => openDrawerForDetails(entry)} className="text-blue-500 hover:underline hover:underline-offset-4 rounded">View</button>
                                <DeleteButton style={`text-red-600 hover:text-red-800 hover:underline hover:underline-offset-4`} onDelete={() => deleteAttendance(entry.id)} />
                                <button
                                    onClick={() => handleEditClick(entry)}
                                    className="text-blue-500 hover:underline">
                                    Edit
                                </button>
                            </td>
                        </tr>
                        ))
                    )}  
                    </tbody>
                </table>
            </div>

            {/* pagination */}
            {!isFiltering && filteredCheckOuts?.length > 0 && (
                <ReactPaginate
                    breakLabel="..."
                    onPageChange={(event) => handlePageChange(event.selected + 1)}
                    forcePage={currentPage - 1}
                    pageRangeDisplayed={5}
                    pageCount={pagination.totalPages}
                    renderOnZeroPageCount={null}
                    nextLabel="Next >"
                    previousLabel="< Prev"
                    previousClassName='dark:text-white'
                    nextClassName="dark:text-white"
                    containerClassName="flex items-center justify-center mt-4"
                    pageClassName="mx-1 px-3 py-1 border rounded"
                    activeClassName="bg-blue-500 text-white"
                />
            )}

            {/* details drawer */}
            <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title="Pending Check-In Details">
                {selectedRequest && (
                    <div className="p-4">
                    <h3 className="text-2xl font-semibold mb-4">Check-Out Request</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                        <div className="font-medium text-gray-600">Employee Name:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.user?.name || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">Employee ID:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.employee_id || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">Check-Out Time:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.check_in_time || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">Check-Out Location:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.check_in_location || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">IP Address:</div>
                        <div className="text-gray-800">{selectedRequest.attendance?.ip_address || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">Device Info:</div>
                        <div className="text-gray-800 break-words">{selectedRequest.attendance?.device_info || "N/A"}</div>
                        </div>

                        <div>
                        <div className="font-medium text-gray-600">Approval Status:</div>
                        <div className="capitalize text-yellow-600">{selectedRequest.approval_status || "pending"}</div>
                        </div>
                    </div>

                    {selectedRequest.attendance?.check_out_image && (
                        <div className="mt-6">
                        <div className="font-medium text-gray-600 mb-2">Check-Out Image:</div>
                        <img
                            src={`https://hrmsnew.prepstripe.com/storage/attendance_images/${selectedRequest.attendance.check_out_image}`}
                            alt="Check-In"
                            className="rounded-md border w-48"
                        />
                        </div>
                    )}

                    <div className="flex items-center mt-6 space-x-4">
                        <button
                        onClick={() => openApprovalModal(selectedRequest, "approved")}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                        >
                        Approve
                        </button>
                        <button
                        onClick={() => openApprovalModal(selectedRequest, "rejected")}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                        >
                        Reject
                        </button>
                    </div>
                    </div>
                )}
            </Drawer>

            {editModalOpen && selectedRequest && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
                <div className="bg-white rounded-t-2xl md:rounded-lg shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">
                {/* <h2 className="text-lg font-semibold mb-4">Edit Attendance</h2> */}

                    <form onSubmit={handleUpdateAttendance} className="bg-white sm:rounded-lg rounded-t-2xl">
                        
                        {/* modal title */}
                        <div className="px-4 py-3 flex border-b border-gray-300">
                            <h2 className="md:text-xl text-md font-semibold">
                                Edit Attendance
                            </h2>
                        </div>
                        
                        <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 rounded-lg bg-white p-4">

                            {/* check out time */}
                            <div>
                                <label className="block mb-1 font-medium">Check Out Time</label>
                                <input
                                    type="datetime-local"
                                    value={editAttendanceData.check_out_time}
                                    onChange={(e) =>
                                        setEditAttendanceData((prev) => ({
                                        ...prev,
                                        check_out_time: e.target.value,
                                        }))
                                    }
                                    required
                                    className="w-full border p-2 rounded"
                                />
                            </div>

                            {/* Add more fields if you want: latitude, longitude, etc */}
                        </div>

                        <div className="px-4 py-5 flex justify-between items-center">
                            <button
                                onClick={() => setEditModalOpen(false)}
                                className="px-4 py-2 bg-gray-400 text-white rounded"
                                >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateAttendance}
                                className="px-4 py-2 bg-blue-600 text-white rounded"
                                >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            )}

            {/* approve modal */}
            {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-50 backdrop-blur-sm">
                <div className="bg-white rounded-t-2xl md:rounded-lg shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">

                    {/* modal title */}
                    <div className="px-4 py-3 flex border-b border-gray-300">
                        <h2 className="md:text-xl text-md font-semibold">
                            You are {selectedStatus} the onboarding form for:{" "}
                            <span className="font-bold">{selectedRequest?.full_name}</span>
                        </h2>
                    </div>
                    
                    {/* modal body */}
                    <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 rounded-lg bg-white p-4">
                        <div className="mb-4 md:text-md text-sm">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                            Remark
                            </label>
                            <textarea
                            className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                            value={approvalComment}
                            onChange={(e) => setApprovalComment(e.target.value)}
                            placeholder="Add any comments..."
                            />
                        </div>
                    </div>

                    <hr className="text-gray-300"/>

                    <div className="px-4 py-5 flex justify-between items-center">
                        <button
                        onClick={closeApprovalModal}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-10 py-2 rounded-md"
                        >
                        Cancel
                        </button>
                        <button
                        onClick={handleApproval}
                        className={`px-4 py-2 rounded-md text-white ${
                            selectedStatus === "approved"
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-red-600 hover:bg-red-700"
                        }`}
                        disabled={isSubmitting}
                        >
                        {isSubmitting ? "Processing..." : selectedStatus}
                        </button>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default CheckOutApprovalPage;
